import { useState, useEffect, useCallback, useRef } from 'react';
import { useSelector } from 'react-redux';
import { getUserInfo, UserInfo } from '../services/auth/authApi';
import { RootState } from '../store';
import { mockAdminProfileImageUrl } from '../utils/postMockData';

// 전역 상태를 관리하기 위한 변수들
let globalUserInfo: UserInfo | null = null;
let globalLoading = false;
let globalError: string | null = null;
let subscribers: (() => void)[] = [];

// 구독자들에게 상태 변경을 알리는 함수
const notifySubscribers = () => {
  // /// console.log('notifySubscribers called, subscribers count:', subscribers.length);
  subscribers.forEach((callback, index) => {
    try {
      // /// console.log(`Calling subscriber ${index}`);
      callback();
    } catch (error) {
      /// console.error(`Error in subscriber ${index}:`, error);
    }
  });
};

// 사용자 정보를 한 번만 로드하는 함수
const loadUserInfoOnce = async (force: boolean = false): Promise<void> => {
  // /// console.log('loadUserInfoOnce called:', { force, globalLoading, hasUserInfo: !!globalUserInfo });
  
  // 강제 로드가 아니고 이미 로딩 중이거나 데이터가 있으면 중복 요청 방지
  if (!force && (globalLoading || globalUserInfo)) {
    // /// console.log('Skipping load - already loading or has data');
    return;
  }

  // 토큰이 없으면 로드하지 않음
  const token = localStorage.getItem('access_token');
  if (!token) {
    // /// console.log('No token available');
    globalUserInfo = null;
    globalLoading = false;
    globalError = null;
    notifySubscribers();
    return;
  }

  // /// console.log('Starting user info load...');
  globalLoading = true;
  globalError = null;
  notifySubscribers();

  try {
    const info = await getUserInfo();
    /// console.log('유저 정보 API 응답:', {info});
    if(info.role === 'ADMIN') {
      info.image_url = mockAdminProfileImageUrl;
      info.name = '투비닥터';
      info.introduction = '관리자';
      info.university = '관리자';
    }
    globalUserInfo = info;
    globalError = null;
  } catch (error) {
    /// console.error('사용자 정보 로드 실패:', error);
    globalError = error instanceof Error ? error.message : '사용자 정보 로드 실패';
    
    // 401 에러인 경우 토큰을 제거하고 로그아웃 처리
    if (error instanceof Error && error.message.includes('401')) {
      localStorage.removeItem('access_token');
      globalUserInfo = null;
    } else {
      // 다른 에러의 경우 기본값으로 처리
      globalUserInfo = {
        id: 0,
        name: '게스트',
        birth: '',
        phone: '',
        email: '',
        identity: 'STUDENT',
        role: '',
        introduction: '',
        image_url: '',
        id_card_file_url: '',
        university: '',
        medicine: '',
        license: '',
        marketing_agreed: false,
        approved: 'NONE'
      };
    }
  } finally {
    // /// console.log('User info load completed');
    globalLoading = false;
    notifySubscribers();
  }
};

// 사용자 정보를 강제로 새로고침하는 함수
const refreshUserInfo = async (): Promise<void> => {
  // /// console.log('refreshUserInfo called - clearing global state and reloading');
  globalUserInfo = null;
  globalError = null;
  globalLoading = false; // 로딩 상태도 초기화
  notifySubscribers(); // 상태 변경을 먼저 알림
  await loadUserInfoOnce(true);
};

// 사용자 정보를 클리어하는 함수 (로그아웃 시 사용)
const clearUserInfo = (): void => {
  globalUserInfo = null;
  globalError = null;
  globalLoading = false;
  notifySubscribers();
};

export const useUserInfo = () => {
  const [userInfo, setUserInfo] = useState<UserInfo | null>(globalUserInfo);
  const [loading, setLoading] = useState<boolean>(globalLoading);
  const [error, setError] = useState<string | null>(globalError);
  const isMountedRef = useRef(true);
  
  // Redux 인증 상태 참고용 (선택적)
  const { isAuthenticated } = useSelector((state: RootState) => state.auth);

  // 컴포넌트가 언마운트될 때 구독 해제
  useEffect(() => {
    return () => {
      isMountedRef.current = false;
    };
  }, []);

  // 상태 변경을 위한 콜백 - 로컬 state 직접 업데이트
  const handleStateChange = useCallback(() => {
    if (isMountedRef.current) {
      // /// console.log('handleStateChange called, global state:', {
      //   globalUserInfo,
      //   globalLoading,
      //   globalError
      // });
      setUserInfo(globalUserInfo);
      setLoading(globalLoading);
      setError(globalError);
    }
  }, []);

  // 컴포넌트 마운트 시 구독 등록 및 데이터 로드
  useEffect(() => {
    // /// console.log('Registering subscriber');
    subscribers.push(handleStateChange);
    
    // 현재 전역 상태를 로컬 상태로 동기화
    setUserInfo(globalUserInfo);
    setLoading(globalLoading);
    setError(globalError);
    
    const accessToken = localStorage.getItem('access_token');
    // /// console.log('useUserInfo mounted:', { 
    //   hasToken: !!accessToken, 
    //   isAuthenticated, 
    //   hasUserInfo: !!globalUserInfo,
    //   isLoading: globalLoading,
    //   subscribersCount: subscribers.length
    // });
    
    // 토큰이 있으면 항상 데이터 로드 시도 (로딩 중이 아닐 때만)
    if (accessToken && !globalLoading) {
      if (!globalUserInfo) {
        // /// console.log('Loading user info because no data exists...');
        loadUserInfoOnce();
      } else {
        // /// console.log('User info already exists, skipping load');
      }
    }
    // 토큰이 없으면 상태 초기화
    else if (!accessToken) {
      // /// console.log('No token, clearing user info');
      globalUserInfo = null;
      globalError = null;
      globalLoading = false;
      notifySubscribers();
    }

    // 클린업: 구독 해제
    return () => {
      // /// console.log('Unregistering subscriber');
      subscribers = subscribers.filter(callback => callback !== handleStateChange);
    };
  }, [handleStateChange]); // isAuthenticated 의존성 제거

  // 현재 상태 로깅
  // /// console.log('useUserInfo returning:', {
  //   userInfo,
  //   loading,
  //   error
  // });

  return {
    userInfo,
    loading,
    error,
    refresh: refreshUserInfo,
    clear: clearUserInfo
  };
};

// 외부에서 직접 호출할 수 있도록 export
export { clearUserInfo, refreshUserInfo };

export default useUserInfo;